import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Support as SupportIcon,
  Assignment as UnassignedIcon,
  Person as MyTicketsIcon,
  PriorityHigh as UrgentIcon,
  ConfirmationNumber as TotalIcon,
  Today as TodayIcon,
  CheckCircle as ResolvedIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowIcon,
  FlashOn as QuickIcon,
  FolderOpen as OpenIcon,
  Autorenew as InProgressIcon,
  DoneAll as DoneIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import adminTicketService from '../../services/adminTicketService';
import { DashboardStats } from '../../types/ticket';
import './TicketStyles.css';

const AdminTicketDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    unassignedTickets: 0,
    myTickets: 0,
    urgentTickets: 0,
    totalTickets: 0,
    todayTickets: 0,
    resolvedThisWeek: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const dashboardData = await adminTicketService.getDashboardStats();
      if (dashboardData) {
        setStats(dashboardData);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null || isNaN(num)) {
      return '۰';
    }
    return new Intl.NumberFormat('fa-IR').format(num);
  };

  const statCards = [
    {
      title: 'تخصیص نیافته',
      value: stats.unassignedTickets,
      icon: <UnassignedIcon />,
      color: 'warning',
      onClick: () => navigate('/tickets/list?unassigned=true')
    },
    {
      title: 'تیکت‌های من',
      value: stats.myTickets,
      icon: <MyTicketsIcon />,
      color: 'info',
      onClick: () => navigate('/tickets/list?assigned_to_me=true')
    },
    {
      title: 'فوری',
      value: stats.urgentTickets,
      icon: <UrgentIcon />,
      color: 'danger',
      onClick: () => navigate('/tickets/list?priority=URGENT')
    },
    {
      title: 'کل تیکت‌ها',
      value: stats.totalTickets,
      icon: <TotalIcon />,
      color: 'primary',
      onClick: () => navigate('/tickets/list')
    },
    {
      title: 'تیکت‌های امروز',
      value: stats.todayTickets,
      icon: <TodayIcon />,
      color: 'secondary',
      onClick: () => navigate('/tickets/list?today=true')
    },
    {
      title: 'حل شده این هفته',
      value: stats.resolvedThisWeek,
      icon: <ResolvedIcon />,
      color: 'success',
      onClick: () => navigate('/tickets/list?resolved_this_week=true')
    }
  ];

  const quickActions = [
    {
      label: 'تیکت‌های باز',
      count: stats.unassignedTickets,
      color: 'warning',
      icon: <OpenIcon style={{ fontSize: 16 }} />,
      onClick: () => navigate('/tickets/list?status=OPEN')
    },
    {
      label: 'در حال بررسی',
      color: 'info',
      icon: <InProgressIcon style={{ fontSize: 16 }} />,
      onClick: () => navigate('/tickets/list?status=IN_PROGRESS')
    },
    {
      label: 'حل شده',
      color: 'success',
      icon: <DoneIcon style={{ fontSize: 16 }} />,
      onClick: () => navigate('/tickets/list?status=RESOLVED')
    },
    {
      label: 'تیکت‌های فوری',
      color: 'danger',
      icon: <WarningIcon style={{ fontSize: 16 }} />,
      onClick: () => navigate('/tickets/list?priority=URGENT')
    }
  ];

  return (
    <>
      <Helmet>
        <title>داشبورد تیکت‌ها</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <SupportIcon />
              داشبورد تیکت‌ها
            </h1>
            <span className="ticket-header-subtitle">
              مدیریت و پیگیری تیکت‌های پشتیبانی
            </span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={loadDashboard} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
            <button className="header-btn primary" onClick={() => navigate('/tickets/list')}>
              مشاهده همه تیکت‌ها
              <ArrowIcon style={{ fontSize: 18, transform: 'rotate(180deg)' }} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <p>در حال بارگذاری...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="stats-grid">
              {statCards.map((stat, index) => (
                <div
                  key={index}
                  className={`stat-card ${stat.color}`}
                  onClick={stat.onClick}
                >
                  <div className="stat-card-header">
                    <div className="stat-card-icon">
                      {stat.icon}
                    </div>
                  </div>
                  <div className="stat-card-title">{stat.title}</div>
                  <div className="stat-card-value">{formatNumber(stat.value)}</div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <div className="quick-actions-title">
                <QuickIcon />
                دسترسی سریع
              </div>
              <div className="quick-actions-grid">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    className={`quick-action-btn ${action.color}`}
                    onClick={action.onClick}
                  >
                    {action.icon}
                    {action.label}
                    {action.count !== undefined && ` (${formatNumber(action.count)})`}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="cards-grid" style={{ marginTop: 24 }}>
              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">پاسخ‌های آماده</h3>
                </div>
                <p className="item-card-content">
                  قالب‌های آماده برای پاسخ سریع به سوالات متداول کاربران
                </p>
                <div className="item-card-meta">
                  <button
                    className="header-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => navigate('/tickets/canned-responses')}
                  >
                    مدیریت پاسخ‌ها
                  </button>
                </div>
              </div>

              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">ماکروها</h3>
                </div>
                <p className="item-card-content">
                  اتوماسیون عملیات تکراری برای مدیریت سریع‌تر تیکت‌ها
                </p>
                <div className="item-card-meta">
                  <button
                    className="header-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => navigate('/tickets/macros')}
                  >
                    مدیریت ماکروها
                  </button>
                </div>
              </div>

              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">گزارشات و متریک‌ها</h3>
                </div>
                <p className="item-card-content">
                  آمار عملکرد تیم پشتیبانی و متریک‌های SLA
                </p>
                <div className="item-card-meta">
                  <button
                    className="header-btn"
                    style={{ marginTop: 8 }}
                    onClick={() => navigate('/tickets/metrics/advanced')}
                  >
                    مشاهده گزارشات
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AdminTicketDashboard;
